import React from 'react';
import { Field } from 'react-final-form';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { useTranslation } from 'react-i18next';
import TextField from '@mui/material/TextField';
import { isValid, formatRFC3339 } from 'services/date-fns';
import fieldRenderProps from 'components/FinalForm/fieldRenderProps';
import getErrorProperties from 'components/FinalForm/getErrorProperties';
import PropTypes from 'prop-types';
import Stack from '@mui/material/Stack';

const parse = (str) => {
  if (!str) {
    return str;
  }

  const date = new Date(str);
  return isValid(date) ? formatRFC3339(date) : str;
};

const toDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return isValid(date) ? date : null;
};

/**
 * Custom TextField slot that appends an extra endAdornment alongside
 * the picker's built-in calendar/clock icon.
 */
const DateTimePickerTextField = React.forwardRef(({ extraEndAdornment, InputProps = {}, ...props }, ref) => (
  <TextField
    ref={ref}
    {...props}
    InputProps={{
      ...InputProps,
      ...(extraEndAdornment ? {
        endAdornment: (
          <Stack direction="row" alignItems="center" spacing={0.5}>
            {InputProps.endAdornment}
            {extraEndAdornment}
          </Stack>
        ),
      } : {}),
    }}
  />
));

DateTimePickerTextField.propTypes = {
  extraEndAdornment: PropTypes.node,
  InputProps: PropTypes.object,
};

const DATE_CONSTRAINT_PROPS = ['minDate', 'maxDate', 'minDateTime', 'maxDateTime', 'minTime', 'maxTime'];

const coerceDateConstraints = (props) => {
  const coerced = {};
  const rest = {};
  Object.keys(props).forEach(key => {
    if (DATE_CONSTRAINT_PROPS.includes(key)) {
      coerced[key] = toDateValue(props[key]);
    } else {
      rest[key] = props[key];
    }
  });
  return { ...rest, ...coerced };
};

const DateTimePickerBase = ({ input: { value, onChange, ...input }, InputLabelProps, meta, fullWidth, margin, endAdornment, variant, shortcuts, ...rest }) => {

  const { t } = useTranslation(['common']);

  const errorProps = getErrorProperties(meta);

  return (
    <DateTimePicker
      // v8 defaults to PickersTextField (with PickersSectionList). Our custom
      // textField slot wraps @mui/material/TextField which has no sectionListRef,
      // so opt back into the v7-compatible DOM structure.
      enableAccessibleFieldDOMStructure={false}
      value={toDateValue(value)}
      onChange={onChange}
      localeText={{
        todayButtonLabel: t('common:datepicker.now', 'Now'),
        okButtonLabel: t('common:datepicker.ok', 'Ok'),
        clearButtonLabel: t('common:datepicker.clear', 'Clear'),
        cancelButtonLabel: t('common:datepicker.cancel', 'Cancel'),
      }}
      {...(endAdornment ? { slots: { textField: DateTimePickerTextField } } : {})}
      slotProps={{
        textField: {
          name: input.name,
          onBlur: input.onBlur,
          onFocus: input.onFocus,
          fullWidth,
          margin,
          variant,
          error: errorProps.error || ((!!value || (input.required && meta.submitFailed)) && !isValid(new Date(value))),
          helperText: errorProps.helperText,
          InputLabelProps: {
            ...input.InputLabelProps,
            ...InputLabelProps,
          },
          ...(endAdornment ? { extraEndAdornment: endAdornment } : {}),
        },
        // Reset theme's MuiDialogActions overrides (negative margins/padding
        // designed for Dialog context) which break the picker popover layout.
        actionBar: {
          sx: { padding: 1, margin: 0 },
        },
        ...(shortcuts ? { shortcuts: { items: shortcuts, changeImportance: 'set' } } : {}),
      }}
      {...coerceDateConstraints(rest)}
    />
  );
};

DateTimePickerBase.propTypes = {
  ...fieldRenderProps,
};

const DateTimePickerField = ({ component = DateTimePickerBase, type = 'string', fullWidth = true, ...rest }) => (
  <Field
    component={component}
    type={type}
    allowNull
    parse={parse}
    fullWidth={fullWidth}
    {...rest}
  />
);

DateTimePickerField.propTypes = {
  component: PropTypes.any,
  type: PropTypes.string,
  fullWidth: PropTypes.bool,
};

export default DateTimePickerField;
